import { useEffect, useRef, useState } from "react";

import { X } from "lucide-react";

import { Input } from "@/components/ui/input";

import { useBillingOrphanSearch, type BillingOrphanResult } from "../../../renewals/api";
import { fmtCur } from "../../../renewals/format";

/**
 * Typeahead over /renewals/accounts/billing-orphans — QB IDs that bill but
 * have no account record. Pre-loads the top results on mount so the picker
 * is useful without typing. Empty `q` returns the most recently billing
 * orphans (browse mode); typing >= 2 chars switches to filtered search.
 */
export function OrphanSearch({
  value,
  onChange,
  excludeQbs,
  id,
}: {
  value: BillingOrphanResult | null;
  onChange: (v: BillingOrphanResult | null) => void;
  /** QB IDs to filter from results — already linked subs + the partner itself. */
  excludeQbs: number[];
  /** Forwarded to the inner input so a <label htmlFor> can associate. */
  id?: string;
}) {
  const [text, setText] = useState(value ? `${value.name} · QB-${value.qb_id}` : "");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setDebounced(text), 250);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [text]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // When a value is picked we suspend the query; clearing or typing resumes it.
  const search = useBillingOrphanSearch(debounced, { enabled: !value });
  const excludeSet = new Set(excludeQbs);
  const results = (search.data?.results ?? []).filter((r) => !excludeSet.has(r.qb_id));

  function pick(r: BillingOrphanResult) {
    onChange(r);
    setText(`${r.name} · QB-${r.qb_id}`);
    setOpen(false);
  }
  function clear() {
    onChange(null);
    setText("");
    setOpen(true);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          if (value) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type a name (e.g. Future Tire) or leave blank to browse…"
        className="pr-8 text-sm"
        autoComplete="off"
      />
      {value && (
        <button
          type="button"
          onClick={clear}
          title="Clear"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && !value && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-md">
          {search.isFetching && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!search.isFetching && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">
              {debounced.length >= 2
                ? "No billing-only QB IDs match."
                : "No billing-only QB IDs available — every counterparty already has an account record or an existing link."}
            </div>
          )}
          {results.map((r) => (
            <button
              key={r.qb_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                pick(r);
              }}
              className="block w-full border-b border-border px-3 py-1.5 text-left text-sm hover:bg-accent"
            >
              <div className="font-medium">{r.name}</div>
              <div className="flex gap-3 text-xs text-muted-foreground">
                <span>QB-{r.qb_id}</span>
                {r.last_sub_mrr > 0 && (
                  <span>
                    last MRR: {fmtCur(r.last_sub_mrr)}
                    {r.last_period && <> ({r.last_period})</>}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
